import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask, restartableTask } from 'ember-concurrency';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-common/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import {
  querySettingsRequest,
  upsertSettingRequest,
  replaceSettingValueRequest,
  type SettingResource,
} from '../requests/setting.ts';
import { PINNED_REPORTS_SUBJECT, isForbidden } from './reports-preferences.ts';

const REPORT_FOLDERS_PREDICATE = 'report-folders';

export interface ReportFolder {
  id: string;
  name: string;
  order: number;
}

interface FoldersValue {
  folders: ReportFolder[];
  assignments: Record<string, string>;
}

/**
 * Personal folders persist as one tio_types.settings row per admin under tio-admin:reports/report-
 * folders. Reports have one assignment; no assignment means Unfiled, and deleted-report keys are
 * ignored when rendering.
 * Writes are optimistic and roll back to the last confirmed state on failure. A 403 disables
 * organization for the session; owner-switching guards mirror ReportsPreferencesService.
 */
export default class ReportFoldersService extends Service {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;

  @tracked folders: ReportFolder[] = [];
  /** reportId -> folderId; absence of a key means Unfiled. */
  @tracked assignments: Record<string, string> = {};
  @tracked private loadedForOwner: string | null = null;
  @tracked private deniedForOwner: string | null = null;

  #settingId: string | null = null;
  #serverState: FoldersValue = { folders: [], assignments: {} };
  #revision = 0;

  get organizingUnavailable() {
    return Boolean(this.ownerId) && this.deniedForOwner === this.ownerId;
  }

  get canOrganize() {
    return (
      Boolean(this.ownerId) && this.loadedForOwner === this.ownerId && !this.organizingUnavailable
    );
  }

  get ownerId() {
    const id = this.sessionContext.user?.id;
    return id ? String(id) : null;
  }

  /** Folders in display order (creation order; ties broken by name). */
  get orderedFolders() {
    return [...this.folders].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  }

  folderIdFor = (reportId: string | null) => this.assignments[String(reportId)] ?? null;

  load = restartableTask(async () => {
    const ownerId = this.ownerId;
    if (!ownerId || this.loadedForOwner === ownerId || this.deniedForOwner === ownerId) return;

    this.folders = [];
    this.assignments = {};
    this.#settingId = null;
    this.#serverState = { folders: [], assignments: {} };
    this.#revision += 1;

    try {
      const { content } = await this.store.request(
        querySettingsRequest({
          ownerType: 'User',
          ownerId,
          subject: PINNED_REPORTS_SUBJECT,
          predicate: REPORT_FOLDERS_PREDICATE,
        })
      );
      if (this.ownerId !== ownerId) return;
      const row = (content as { data?: SettingResource[] })?.data?.[0];
      if (row) {
        this.#settingId = row.id;
        const value = parseFoldersValue(row.attributes?.value);
        this.folders = value.folders;
        this.assignments = value.assignments;
      }
      this.#serverState = { folders: this.folders, assignments: this.assignments };
      this.loadedForOwner = ownerId;
    } catch (error) {
      this.#handleError(error, { notify: false, ownerId });
    }
  });

  createFolder = (name: string): ReportFolder | null => {
    const trimmed = name.trim();
    if (!this.canOrganize || !trimmed) return null;
    const nextOrder = this.folders.reduce((max, f) => Math.max(max, f.order), -1) + 1;
    const folder: ReportFolder = { id: crypto.randomUUID(), name: trimmed, order: nextOrder };
    this.folders = [...this.folders, folder];
    this.#commit();
    return folder;
  };

  renameFolder = (folderId: string, name: string) => {
    const trimmed = name.trim();
    if (!this.canOrganize || !trimmed) return;
    if (!this.folders.some((f) => f.id === folderId)) return;
    this.folders = this.folders.map((f) => (f.id === folderId ? { ...f, name: trimmed } : f));
    this.#commit();
  };

  /** Deleting a folder reverts its contents to Unfiled. */
  deleteFolder = (folderId: string) => {
    if (!this.canOrganize) return;
    if (!this.folders.some((f) => f.id === folderId)) return;
    this.folders = this.folders.filter((f) => f.id !== folderId);
    this.assignments = Object.fromEntries(
      Object.entries(this.assignments).filter(([, assigned]) => assigned !== folderId)
    );
    this.#commit();
  };

  /** folderId null moves the report back to Unfiled. */
  moveReport = (reportId: string | null, folderId: string | null) => {
    if (!this.canOrganize || reportId == null) return;
    const id = String(reportId);
    if (folderId === null) {
      if (!(id in this.assignments)) return;
      const { [id]: _dropped, ...rest } = this.assignments;
      this.assignments = rest;
    } else {
      if (!this.folders.some((f) => f.id === folderId)) return;
      if (this.assignments[id] === folderId) return;
      this.assignments = { ...this.assignments, [id]: folderId };
    }
    this.#commit();
  };

  #commit() {
    this.#revision += 1;
    void this.persist.perform();
  }

  /** keepLatest: rapid mutation bursts coalesce into one trailing write. */
  persist = keepLatestTask(async () => {
    const ownerId = this.ownerId;
    if (!ownerId || this.loadedForOwner !== ownerId) return;
    const revision = this.#revision;
    const state: FoldersValue = { folders: this.folders, assignments: this.assignments };
    const value = { folders: state.folders, assignments: state.assignments } as unknown as Record<
      string,
      unknown
    >;
    try {
      if (this.#settingId) {
        await this.store.request(replaceSettingValueRequest(this.#settingId, value));
      } else {
        const { content } = await this.store.request(
          upsertSettingRequest({
            ownerResourceType: 'users',
            ownerId,
            subject: PINNED_REPORTS_SUBJECT,
            predicate: REPORT_FOLDERS_PREDICATE,
            value,
          })
        );
        if (this.loadedForOwner !== ownerId) return;
        this.#settingId = (content as { data?: SettingResource })?.data?.id ?? null;
      }
      if (this.loadedForOwner !== ownerId) return;
      this.#serverState = state;
    } catch (error) {
      if (this.loadedForOwner === ownerId && this.#revision === revision) {
        this.folders = this.#serverState.folders;
        this.assignments = this.#serverState.assignments;
      }
      this.#handleError(error, { notify: true, ownerId });
    }
  });

  #handleError(error: unknown, { notify, ownerId }: { notify: boolean; ownerId: string }) {
    if (isForbidden(error)) {
      this.deniedForOwner = ownerId;
      return;
    }
    console.error('[report-folders] settings request failed', error);
    if (notify && this.ownerId === ownerId) {
      this.notifications.add('Failed to save report folders', { appearance: 'error' });
    }
  }
}

/** Defensive parse: malformed rows (older versions, manual edits) degrade to
 * empty state instead of crashing the rail. */
function parseFoldersValue(raw: Record<string, unknown> | undefined): FoldersValue {
  const folders: ReportFolder[] = [];
  const rawFolders = raw?.['folders'];
  if (Array.isArray(rawFolders)) {
    rawFolders.forEach((entry, index) => {
      const candidate = entry as { id?: unknown; name?: unknown; order?: unknown } | null;
      if (candidate && typeof candidate.id === 'string' && typeof candidate.name === 'string') {
        // Trim and require both: createFolder/renameFolder already reject
        // blank names, so a blank one here means a hand-edited or legacy row.
        // Left as-is it would render an unclickable empty rail entry and make
        // folderIdFor resolve to a folder with no displayable name.
        const id = candidate.id.trim();
        const name = candidate.name.trim();
        if (!id || !name) return;

        const order = typeof candidate.order === 'number' ? candidate.order : index;
        folders.push({ id, name, order });
      }
    });
  }
  const assignments: Record<string, string> = {};
  const rawAssignments = raw?.['assignments'];
  if (rawAssignments && typeof rawAssignments === 'object' && !Array.isArray(rawAssignments)) {
    for (const [reportId, folderId] of Object.entries(rawAssignments as Record<string, unknown>)) {
      const trimmed = typeof folderId === 'string' ? folderId.trim() : null;
      if (trimmed && folders.some((f) => f.id === trimmed)) {
        assignments[String(reportId)] = trimmed;
      }
    }
  }
  return { folders, assignments };
}
